import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, KeyRound } from 'lucide-react';
import { api, timeAgo } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { useGuild } from './GuildLayout.jsx';

function channelLabel(c) {
  if (c.type === 2)  return `🔊 ${c.name}`;
  if (c.type === 13) return `🎙️ ${c.name}`;
  if (c.type === 5)  return `📣 ${c.name}`;
  return `# ${c.name}`;
}

export default function ChannelMods() {
  const { guildId, channels } = useGuild();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [savingRole, setSavingRole] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newChan, setNewChan] = useState('');
  const [newUser, setNewUser] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); api.guildRoles(guildId).then(r => setRoles(r.roles)); }, [guildId]);
  function reload() {
    setData(null);
    api.listChannelMods(guildId).then(setData).catch(err => setData({ error: err.message }));
  }
  function fireToast(type, msg) { setToast({ type, msg }); setTimeout(() => setToast(null), 2500); }

  async function saveRole(roleId) {
    setSavingRole(true);
    try {
      await api.putConfig(guildId, { channel_mods_role_id: roleId || null });
      setData(d => ({ ...d, role_id: roleId || null }));
      fireToast('success', roleId ? 'Role linked' : 'Role unlinked');
    } catch (err) { fireToast('error', err.message); }
    finally { setSavingRole(false); }
  }

  async function add() {
    if (!newChan || !newUser) return;
    try {
      await api.addChannelMod(guildId, { channel_id: newChan, user_id: newUser.trim() });
      setNewChan(''); setNewUser(''); setAdding(false);
      reload();
      fireToast('success', 'Added — DM sent to the new mod');
    } catch (err) { fireToast('error', err.message); }
  }

  async function remove(mod) {
    if (!confirm(`Remove this user as mod of #${channels.find(c => c.id === mod.channel_id)?.name || mod.channel_id}?`)) return;
    try {
      await api.removeChannelMod(guildId, { channel_id: mod.channel_id, user_id: mod.user_id });
      reload();
      fireToast('success', 'Removed');
    } catch (err) { fireToast('error', err.message); }
  }

  if (!data) return <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  // Group mods by channel
  const grouped = new Map();
  for (const m of data.mods) {
    if (!grouped.has(m.channel_id)) grouped.set(m.channel_id, []);
    grouped.get(m.channel_id).push(m);
  }

  return (
    <>
      <h1>Channel Mods</h1>
      <p className="subtitle">Designate per-channel moderators who can warn, mute, or jail only within their assigned channel.</p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Channel Mods role</h3>
        <p className="muted" style={{ marginTop: '-.5rem', fontSize: 13 }}>Optional. SoupOS auto-grants this role to anyone added as channel-mod and removes it when their last channel-mod assignment is revoked. Lets you see all channel-mods together in the Discord member list.</p>
        <div className="field" style={{ marginBottom: 0 }}>
          <select value={data.role_id || ''} onChange={e => saveRole(e.target.value)} disabled={savingRole}>
            <option value="">— none —</option>
            {roles.map(r => (
              <option key={r.id} value={r.id} disabled={!r.assignable}>
                {r.name}{!r.assignable ? ' (too high — drag SoupOS above this role)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row actions" style={{ marginBottom: '1rem' }}>
        <button onClick={() => setAdding(true)}><Plus size={14} strokeWidth={2.5} /> Add channel mod</button>
      </div>

      {!grouped.size ? (
        <div className="card empty">
          <div className="ico"><KeyRound size={32} strokeWidth={1.5} /></div>
          <p>No channel mods yet. Click "Add channel mod" to give a member moderation powers in a specific channel.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {[...grouped.entries()].map(([channelId, mods]) => {
            const channel = channels.find(c => c.id === channelId);
            return (
              <div className="card" key={channelId}>
                <h3 style={{ marginTop: 0 }}>
                  {channel ? channelLabel(channel) : <span className="dim">channel missing</span>}
                  <span className="muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>{mods.length} mod{mods.length === 1 ? '' : 's'}</span>
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {mods.map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.5rem .75rem', background: 'var(--bg-elev-2)', borderRadius: 6 }}>
                      <UserChip guildId={guildId} userId={m.user_id} />
                      <span className="dim" style={{ fontSize: 12 }}>added {timeAgo(m.created_at)} by <UserChip guildId={guildId} userId={m.granted_by} size={16} /></span>
                      <button className="ghost danger-text" style={{ marginLeft: 'auto', padding: '.3rem .55rem' }} onClick={() => remove(m)}>
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="muted" style={{ marginTop: '2rem', fontSize: 13 }}>
        Looking for active channel mutes and jails? They live on the <Link to={`/g/${guildId}/restrictions`}>Restrictions</Link> page with the server-wide ones.
      </p>

      {adding && (
        <div className="modal-overlay" onClick={() => setAdding(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Add channel mod</h3>
            <p className="muted" style={{ marginTop: '-.25rem' }}>The user will receive a DM explaining their new powers and the rules.</p>
            <div className="field">
              <label>Channel</label>
              <select value={newChan} onChange={e => setNewChan(e.target.value)}>
                <option value="">— pick a channel —</option>
                {channels.map(c => <option key={c.id} value={c.id}>{channelLabel(c)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>User ID</label>
              <input value={newUser} onChange={e => setNewUser(e.target.value)} placeholder="Discord user ID (right-click → Copy User ID)" />
              <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>Enable Discord Developer Mode (Settings → Advanced) to get Copy User ID on the right-click menu.</p>
            </div>
            <div className="row actions" style={{ gap: '.5rem' }}>
              <button type="button" className="ghost" onClick={() => setAdding(false)}>Cancel</button>
              <button type="button" onClick={add} disabled={!newChan || !newUser}>Add</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
